// The game home's Getting started card (spec/dashboard.md section 3.1, the game home; section 12.2, releases and pins): the
// five steps from an empty game to a device reading content (the schema, content, a release, a dev pin, the public key in the
// SDK), each with a done mark computed from data and a link to the place. Shown until every step is done; "Hide" remembers
// the choice per game in `localStorage['hue.onboarding:<gameId>']`. A read that fails leaves its step undetermined.
import { useState } from 'react';
import { Link } from 'react-router';
import { ops } from '../api/operations';
import type { Page, Player, Release, Template } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { useGame } from '../shell/GameContext';
import { useReleaseStatus } from '../shell/releaseStatus';
import { routes } from '../shell/routes';
import { IconButton } from '../ui/Button';
import { Check, CheckCircle2, Copy } from '../ui/icons';
import { useQuery } from '../workspace/cache';

/** `loading` while the read runs, `unknown` when it failed ("could not check"). */
export type StepState = 'loading' | 'done' | 'open' | 'unknown';

export interface OnboardingStep {
  key: 'schema' | 'content' | 'release' | 'pin' | 'connect';
  title: string;
  sentence: string;
  state: StepState;
  link: { to: string; label: string };
}

/** The storage key of the "Hide" choice of a game. */
export const onboardingKey = (gameId: string) => `hue.onboarding:${gameId}`;

export function readOnboardingHidden(gameId: string): boolean {
  try {
    return localStorage.getItem(onboardingKey(gameId)) === 'hidden';
  } catch {
    return false;
  }
}

export function writeOnboardingHidden(gameId: string, hidden: boolean): void {
  try {
    if (hidden) localStorage.setItem(onboardingKey(gameId), 'hidden');
    else localStorage.removeItem(onboardingKey(gameId));
  } catch {
    // storage unavailable: the choice lasts for this page only
  }
}

const stateOf = (q: { data: unknown; error: unknown; loading: boolean }, done: (data: never) => boolean): StepState => {
  if (q.data !== undefined) return done(q.data as never) ? 'done' : 'open';
  if (q.error !== undefined) return 'unknown';
  return 'loading';
};

/** The cache key of the game's newest releases (the checklist's release step and the home's Last releases share it). */
export const homeReleasesKey = (org: string, game: string) => `game:${org}/${game}:home:releases`;

/** The newest five releases of the game, shared by the checklist and the Last releases row. */
export function useHomeReleases() {
  const { org, game } = useGame();
  const { client } = useAuth();
  return useQuery(homeReleasesKey(org, game.slug), () => client.data(ops.listReleases, { path: { org, game: game.slug }, query: { limit: 5 } }) as unknown as Promise<Page<Release>>);
}

/** The five steps and whether each is done. */
export function useOnboarding(): { steps: OnboardingStep[]; allDone: boolean } {
  const { org, game, branches, environments } = useGame();
  const { client } = useAuth();
  const defaultBranch = branches.find((b) => b.isDefault) ?? branches[0];
  const bname = defaultBranch?.name ?? null;
  const head = defaultBranch?.head ?? null;
  const base = `game:${org}/${game.slug}:onboarding`;

  const templatesQ = useQuery(bname ? `${base}:templates:${bname}` : null, () => client.data(ops.listTemplates, { path: { org, game: game.slug, branch: bname! }, query: { limit: 1 } }) as unknown as Promise<Page<Template>>, { head });
  const documentsQ = useQuery(bname ? `${base}:documents:${bname}` : null, () => client.data(ops.listDocuments, { path: { org, game: game.slug, branch: bname! }, query: { limit: 1 } }) as unknown as Promise<Page<unknown>>, { head });
  const releasesQ = useHomeReleases();
  const status = useReleaseStatus();
  // a recent player on dev, then stage, then prod: done at the first environment that has one; undetermined when every read failed
  const order = ['dev', 'stage', 'prod'].flatMap((k) => environments.filter((e) => e.kind === k));
  const playersQ = useQuery(order.length ? `${base}:players` : null, async (): Promise<StepState> => {
    let failed = 0;
    for (const env of order) {
      try {
        const page = (await client.data(ops.recentPlayers, { path: { org, game: game.slug, env: env.slug }, query: { limit: 1 } })) as unknown as Page<Player>;
        if ((page.items ?? []).length > 0) return 'done';
      } catch {
        failed += 1;
      }
    }
    return failed === order.length ? 'unknown' : 'open';
  });

  const dev = environments.find((e) => e.kind === 'dev');
  const devStatus = status.envs?.find((e) => e.env.id === dev?.id);
  const pinState: StepState = !dev
    ? 'unknown'
    : devStatus
      ? devStatus.forbidden
        ? 'unknown'
        : devStatus.pin
          ? 'done'
          : 'open'
      : status.loading
        ? 'loading'
        : 'unknown';

  const b = bname ?? 'main';
  const firstTemplate = templatesQ.data?.items?.[0]?.name;
  const steps: OnboardingStep[] = [
    {
      key: 'schema',
      title: 'Create or import your schema',
      sentence: 'Templates describe the shape of your content: add one in Data structure or import a Balancy export.',
      state: bname ? stateOf(templatesQ, (d: Page<Template>) => (d.items ?? []).length > 0) : 'unknown',
      link: { to: routes.templates(org, game.slug, b), label: 'Data structure' },
    },
    {
      key: 'content',
      title: 'Add content',
      sentence: 'Documents are the rows of your templates: the items, offers and settings the game reads.',
      state: bname ? stateOf(documentsQ, (d: Page<unknown>) => (d.items ?? []).length > 0) : 'unknown',
      link: { to: firstTemplate ? routes.documents(org, game.slug, b, firstTemplate) : routes.templates(org, game.slug, b), label: 'Documents' },
    },
    {
      key: 'release',
      title: 'Build a release',
      sentence: 'A release is an immutable snapshot of a branch that environments can serve.',
      state: stateOf(releasesQ, (d: Page<Release>) => (d.items ?? []).length > 0),
      link: { to: `${routes.releases(org, game.slug, b, 'releases')}?build=1`, label: 'Build release' },
    },
    {
      key: 'pin',
      title: 'Pin it on dev',
      sentence: 'Pinning a release on an environment makes it what the devices of that environment download.',
      state: pinState,
      link: { to: routes.releases(org, game.slug, b, 'environments'), label: 'Environments' },
    },
    {
      key: 'connect',
      title: 'Connect the game',
      sentence: "Put it into the SDK's config; done once a device signs in.",
      state: playersQ.data ?? (playersQ.error !== undefined ? 'unknown' : order.length ? 'loading' : 'unknown'),
      link: { to: routes.players(org, game.slug, dev?.slug ?? 'dev'), label: 'Players' },
    },
  ];
  return { steps, allDone: steps.every((s) => s.state === 'done') };
}

function PublicKey() {
  const { game } = useGame();
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard?.writeText(game.publicKey ?? '').then(() => setCopied(true));
  };
  return (
    <span className="onboarding-key">
      <code>{game.publicKey}</code>
      <IconButton label="Copy the public key" onClick={copy}>
        {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
      </IconButton>
    </span>
  );
}

/** The Getting started card: the numbered steps, done ones checked and muted; "Hide" calls `onHide`. */
export function GettingStarted({ steps, onHide }: { steps: OnboardingStep[]; onHide: () => void }) {
  const done = steps.filter((s) => s.state === 'done').length;
  return (
    <section className="pane onboarding" aria-labelledby="onboarding-title" data-testid="getting-started">
      <div className="onboarding-head">
        <h2 id="onboarding-title">Getting started</h2>
        <span className="muted">
          {done} of {steps.length} done
        </span>
        <button type="button" className="link-button onboarding-hide" onClick={onHide}>
          Hide
        </button>
      </div>
      <ol className="onboarding-steps">
        {steps.map((s, i) => (
          <li key={s.key} className={`onboarding-step is-${s.state}`} data-testid={`onboarding-${s.key}`} data-state={s.state}>
            <span className="onboarding-mark" aria-hidden="true">
              {s.state === 'done' ? <CheckCircle2 /> : i + 1}
            </span>
            <div className="onboarding-body">
              <div className="onboarding-title">
                <strong>{s.title}</strong>
                {s.state === 'done' && <span className="onboarding-sr"> (done)</span>}
                {s.state === 'unknown' && <span className="muted onboarding-unknown">could not check</span>}
              </div>
              {s.state === 'loading' ? (
                <span className="skeleton onboarding-skeleton" role="status" aria-label={`Checking: ${s.title}`} />
              ) : (
                <p className="onboarding-sentence">{s.sentence}</p>
              )}
              {s.key === 'connect' && <PublicKey />}
            </div>
            <Link className="onboarding-link" to={s.link.to}>
              {s.link.label}
            </Link>
          </li>
        ))}
      </ol>
    </section>
  );
}
